import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Plus, Loader2, RefreshCw, Timer, Search, Download, CheckSquare, X as XIcon, UserPlus } from 'lucide-react';
import { serviceRequestAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../utils/timezone';
import Paginator from '../components/ui/Paginator';

const MIN = 60 * 1000; const HOUR = 60 * MIN;
function formatCountdown(ms) { const abs = Math.abs(ms); const sign = ms < 0 ? '-' : ''; const h = Math.floor(abs/HOUR); const m = Math.floor((abs%HOUR)/MIN); const s = Math.floor((abs%MIN)/1000); if (h>0) return `${sign}${h}h ${m}m ${s}s`; if (m>0) return `${sign}${m}m ${s}s`; return `${sign}${s}s`; }

function SLATimer({ due }) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => { if (!due) return; const u = () => setRemaining(new Date(due).getTime()-Date.now()); u(); const i = setInterval(u, 1000); return () => clearInterval(i); }, [due]);
  if (!due) return <span className="text-xs text-slate-400">N/A</span>;
  const breached = remaining < 0; const atRisk = remaining > 0 && remaining < 30*MIN;
  const color = breached ? 'text-rose-600' : atRisk ? 'text-amber-600' : 'text-emerald-600';
  const bg = breached ? 'bg-rose-50 ring-rose-200' : atRisk ? 'bg-amber-50 ring-amber-200' : 'bg-emerald-50 ring-emerald-200';
  return (<div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${bg} ring-1`}><Timer className={`w-3 h-3 ${color}`} /><span className={`text-xs font-mono font-semibold ${color} tabular-nums`}>{formatCountdown(remaining)}</span></div>);
}

const urgColors = { asap: 'bg-rose-100 text-rose-800', this_week: 'bg-amber-100 text-amber-800', standard: 'bg-blue-100 text-blue-800', no_rush: 'bg-slate-200 text-slate-700' };
const stClass = { new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800', in_progress: 'bg-emerald-100 text-emerald-800', pending_approval: 'bg-purple-100 text-purple-800', resolved: 'bg-emerald-200 text-emerald-900', closed: 'bg-slate-200 text-slate-600' };

export default function ServiceRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [, setTick] = useState(0);

  useEffect(() => { const i = setInterval(() => setTick(t => t+1), 1000); return () => clearInterval(i); }, []);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      const [res, uRes] = await Promise.all([serviceRequestAPI.list(params), userAPI.list({ page_size: 500 })]);
      setRequests(res.data?.results || res.data || []);
      setTotal(res.data?.count || 0);
      setUsers(uRes.data?.results || uRes.data || []);
    } catch { setRequests([]); }
    finally { setLoading(false); setSelected(new Set()); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, [page, pageSize]);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === requests.length ? new Set() : new Set(requests.map(r => r.id)));

  const handleBulk = async (action, extra = {}) => {
    try {
      await Promise.all([...selected].map(id => {
        if (action === 'assign') return serviceRequestAPI.assign(id, { assigned_to: extra.assigned_to });
        if (action === 'status') return serviceRequestAPI.updateStatus(id, extra.status);
        return Promise.resolve();
      }));
      setBulkAssignTo(''); setSelected(new Set()); fetch();
    } catch { fetch(); }
  };

  const exportCSV = () => {
    const data = requests.filter(r => selected.size === 0 || selected.has(r.id));
    const headers = ['Ticket', 'Title', 'Urgency', 'Status', 'Assignee', 'Application', 'Created', 'Updated'];
    const rows = data.map(r => [r.ticket_number, r.title, r.urgency_level, r.status, r.assigned_to_name || '', r.application_name || '', r.created_at ? new Date(r.created_at).toLocaleString() : '', r.updated_at ? new Date(r.updated_at).toLocaleString() : '']);
    const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `service_requests_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="service-requests-page">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <Settings className="w-6 h-6 text-blue-600" /> Service Requests
        </h2>
        <div className="flex gap-2">
          <button onClick={fetch} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-3 py-2 rounded-xl text-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-slate-300"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => navigate('/new-request')} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="new-sr-btn"><Plus className="w-4 h-4" /> New SR</button>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded-xl text-xs mb-4 flex items-center gap-2 text-blue-800 dark:text-blue-300">
        <Settings className="w-4 h-4 flex-shrink-0" />
        <span>SR priority = Urgency × Business need. Types: <strong>ASAP</strong> (same-day), <strong>This Week</strong> (within 5 days), <strong>Standard</strong> (within SLA window), <strong>No Rush</strong> (low priority)</span>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <form onSubmit={e => { e.preventDefault(); fetch(); }} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2.5 border dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 dark:text-white" /></div>
          <button type="submit" className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-sm dark:text-white">Search</button>
        </form>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/30 px-4 py-2 rounded-xl border border-sky-200 dark:border-sky-700 flex-wrap">
            <CheckSquare className="w-4 h-4 text-sky-600" />
            <span className="text-sm font-medium text-sky-700 dark:text-sky-400">{selected.size} selected</span>
            {!isEndUser && (
              <>
                <select value={bulkAssignTo} onChange={e => setBulkAssignTo(e.target.value)} className="border dark:border-slate-700 rounded-lg px-2 py-1 text-xs dark:bg-slate-800 dark:text-white">
                  <option value="">Assign to...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
                {bulkAssignTo && <button onClick={() => handleBulk('assign', { assigned_to: bulkAssignTo })} className="bg-sky-500 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1"><UserPlus className="w-3 h-3" /> Assign</button>}
                <button onClick={() => handleBulk('status', { status: 'resolved' })} className="bg-emerald-500 text-white px-2 py-1 rounded-lg text-xs">Fulfilled</button>
                <button onClick={() => handleBulk('status', { status: 'in_progress' })} className="bg-amber-500 text-white px-2 py-1 rounded-lg text-xs">In Progress</button>
              </>
            )}
            <button onClick={exportCSV} className="bg-slate-600 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-slate-600"><XIcon className="w-4 h-4" /></button>
          </div>
        )}
        {selected.size === 0 && (
          <button onClick={exportCSV} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-3 py-2 rounded-xl text-sm hover:bg-slate-50 flex items-center gap-1.5 dark:text-slate-300">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <Settings className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No service requests found</p>
          <button onClick={() => navigate('/new-request')} className="mt-4 bg-sky-500 text-white px-4 py-2 rounded-xl text-sm">Create First SR</button>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
            <table className="w-full" data-testid="sr-table">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-3 w-10"><input type="checkbox" checked={selected.size === requests.length && requests.length > 0} onChange={toggleAll} className="rounded" /></th>
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-left font-semibold">Title</th>
                  <th className="px-4 py-3 text-center font-semibold">Urgency</th>
                  <th className="px-4 py-3 text-left font-semibold">Assigned To</th>
                  <th className="px-4 py-3 text-center font-semibold">Status</th>
                  <th className="px-4 py-3 text-center font-semibold">SLA</th>
                  <th className="px-4 py-3 text-left font-semibold">Created</th>
                  <th className="px-4 py-3 text-left font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700 text-sm">
                {requests.map(sr => (
                  <tr key={sr.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer" data-testid={`row-${sr.ticket_number}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(sr.id)} onChange={() => toggleSelect(sr.id)} onClick={e => e.stopPropagation()} className="rounded" /></td>
                    <td className="px-4 py-3 font-mono text-blue-700 dark:text-blue-400 font-semibold" onClick={() => navigate(`/ticket/sr/${sr.id}`)}>{sr.ticket_number}</td>
                    <td className="px-4 py-3 dark:text-white" onClick={() => navigate(`/ticket/sr/${sr.id}`)}>{sr.title}</td>
                    <td className="px-4 py-3 text-center"><span className={`${urgColors[sr.urgency_level] || 'bg-slate-100'} px-2.5 py-1 rounded-full text-xs font-semibold`}>{sr.urgency_level?.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{sr.assigned_to_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                    <td className="px-4 py-3 text-center"><span className={`${stClass[sr.status] || 'bg-slate-100 text-slate-700'} px-2 py-1 rounded-full text-xs`}>{sr.status?.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-center">
                      {(['resolved','closed','cancelled'].includes(sr.status) || sr.sla_paused || sr.sla_resolution_met !== null)
                        ? <span className={`text-xs px-2 py-1 rounded-lg ${sr.status === 'cancelled' || sr.sla_paused ? 'text-slate-500 bg-slate-100 dark:bg-slate-700 dark:text-slate-400' : sr.sla_resolution_met === false ? 'text-rose-600 bg-rose-50' : sr.sla_resolution_met === true ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-50'}`}>
                            {sr.status === 'cancelled' ? 'Cancelled' : sr.sla_paused ? 'Stopped' : sr.sla_resolution_met === false ? 'Breached' : sr.sla_resolution_met === true ? 'Met' : 'Closed'}
                          </span>
                        : <SLATimer due={sr.resolution_due} />}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{formatDateTime(sr.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{formatDateTime(sr.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3">
            {requests.map(sr => (
              <div key={sr.id} onClick={() => navigate(`/ticket/sr/${sr.id}`)}
                className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 shadow-sm cursor-pointer" data-testid={`row-${sr.ticket_number}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className="font-mono text-blue-700 dark:text-blue-400 font-bold text-sm">{sr.ticket_number}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <span className={`${urgColors[sr.urgency_level] || 'bg-slate-100'} px-2 py-0.5 rounded-full text-xs font-semibold`}>{sr.urgency_level?.replace(/_/g, ' ')}</span>
                    <span className={`${stClass[sr.status] || 'bg-slate-100'} px-2 py-0.5 rounded-full text-xs`}>{sr.status?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <div className="font-medium text-slate-800 dark:text-white text-sm mb-1">{sr.title}</div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{sr.assigned_to_name || 'Unassigned'} · {formatDateTime(sr.created_at)}</span>
                  {!['cancelled'].includes(sr.status) && !sr.sla_paused ? <SLATimer due={sr.resolution_due} /> : <span className="text-xs text-slate-400">{sr.status === 'cancelled' ? 'Withdrawn' : 'SLA Stopped'}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Paginator total={total} page={page} pageSize={pageSize} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </div>
        </>
      )}
    </div>
  );
}
